import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useDebounce } from 'use-debounce';
import { ComboBox, IComboBox, IComboBoxOption, Label, SearchBox, SelectableOptionMenuItemType, Stack } from "@fluentui/react";
import { Pagination } from '@fluentui/react-experiments';

import { ArrowClockwise24Filled, FilterDismiss24Filled } from "@fluentui/react-icons";
import { animated, useSpring } from "@react-spring/web";

import styles from "./FileStatus.module.css";

import { getToken } from "../../authConfig";
import { handleError, handleSuccess } from "../../toaster";
import { getUploadStatus, FileUploadStatus, updateFiles, UserData, FileUpdateData } from "../../api";
import { DocumentsDetailList, IDocument } from "./DocumentsDetailList";

interface Props {
    className?: string;
    selectableFolders: IComboBoxOption[];
    selectableTags: IComboBoxOption[];
    userData: UserData;
    useDarkMode: boolean;
};

export const FileStatus = ({ className, selectableFolders, selectableTags, userData, useDarkMode }: Props) => {
    const client = useMsal().instance;
    const [selectedFileStates, setSelectedFileStates] = useState<string[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
    const [selectableFolderOptions, setSelectableFolderOptions] = useState<IComboBoxOption[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectableTagOptions, setSelectableTagOptions] = useState<IComboBoxOption[]>([]);
    const [fileNameSearch, setFileNameSearch] = useState<string>("");

    const [files, setFiles] = useState<IDocument[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [totalCount, setTotalCount] = useState<number>(0);

    // Pagination
    const itemsPerPage = 25;
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const totalRecords = files.length;
    const firstRecordOnPage = indexOfFirstItem + 1;
    const lastRecordOnPage = Math.min(indexOfLastItem, totalRecords);

    const animatedStyles = useSpring({ from: { opacity: 0 }, to: { opacity: 1 } });

    const [debouncedFileStates] = useDebounce(selectedFileStates, 300);
    const [debouncedFolders] = useDebounce(selectedFolders, 300);
    const [debouncedTags] = useDebounce(selectedTags, 300);
    const [debouncedFileNameSearch] = useDebounce(fileNameSearch, 300);

    const selectableFileStateOptions: IComboBoxOption[] = [
        { key: 'selectAll', text: 'Select All', itemType: SelectableOptionMenuItemType.SelectAll },
        { key: 'Complete', text: 'Complete' },
        { key: 'Deleted', text: 'Deleted' },
        { key: 'Error', text: 'Error' },
        { key: 'Processing', text: 'Processing' },
        { key: 'Indexing', text: 'Indexing' },
        { key: 'Queued', text: 'Queued' },
        { key: 'Skipped', text: 'Skipped' },
        { key: 'Uploaded', text: 'Uploaded' },
        { key: 'Throttled', text: 'Throttled' },
    ];

    const FILE_ICONS: { [id: string]: string } = {
        "csv": 'csv',
        "doc": 'docx',
        "docx": 'docx',
        "pdf": 'pdf',
        "pptx": 'pptx',
        "txt": 'txt',
        "htm": 'html',
        "html": 'html',
        "xls": 'xlsx',
        "xlsx": 'xlsx',
        "xml": 'xml'
    };

    const STATE_DESCRIPTION: { [id: string]: string } = {
        "Processing": "File is being processed, please check back later",
        "Skipped": "File processing was skipped",
        "Queued": "File is queued for processing, please check back later",
        "Complete": "File processing is complete",
        "Error": "There was an unexpected error processing the file"
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const token = client ? await getToken(client) : undefined;
            const response = await getUploadStatus(
                token,
                (currentPage - 1) * itemsPerPage,
                itemsPerPage,
                debouncedFileStates.includes('selectAll') ? [] : debouncedFileStates,
                debouncedFolders.includes('selectAll') ? [] : debouncedFolders,
                debouncedTags.includes('selectAll') ? [] : debouncedTags,
                debouncedFileNameSearch
            );

            const list = convertStatusToItems(response.records);
            setFiles(list);
            setTotalCount(response.total_count);
        } catch (error) {
            handleError(error, "An error occurred while fetching file list");
        } finally {
            setIsLoading(false);
        }
    };

    const onFilesSorted = (items: IDocument[]): void => {
        setFiles(items);
    };

    const onFileDelete = async (items: IDocument[]) => {
        try {
            setIsDeleting(true);

            // Update files list with new status for immediate display
            setFiles((files) =>
                files.map((file) =>
                    items.find(item => item.key === file.key) ? {
                        ...file,
                        state: "Deleted",
                    } : file
                )
            );

            const token = client ? await getToken(client) : undefined;
            const deleteData: FileUpdateData[] = items.map(item => ({
                action: "delete",
                file_path: item.file_path
            }))
            await updateFiles(token, deleteData)
            handleSuccess("File(s) deleted successfully!")
        } catch (error) {
            handleError(error, "Error deleting item");
        } finally {
            setIsDeleting(false);
        }
    };

    const onSaveTags = async (items: IDocument[]) => {
        try {
            // Update files list with new tags for immediate display
            setFiles((files) =>
                files.map((file) => {
                    const matchingItem = items.find(item => item.key === file.key);
                    return matchingItem ? {
                        ...file,
                        tags: matchingItem.tags,
                    } : file;
                })
            );
            const token = client ? await getToken(client) : undefined;
            const tagsData: FileUpdateData[] = items.map(item => ({
                action: "updateTags",
                file_path: item.file_path,
                tags: item.tags,
            }));

            await updateFiles(token, tagsData);
            handleSuccess("Tags updated successfully!")
        } catch (error) {
            handleError(error, "Error updating tags");
        }
    };

    const onSavePermissions = async (items: IDocument[]) => {
        try {
            setFiles((files) =>
                files.map((file) =>
                    items.find(item => item.key === file.key) ? {
                        ...file,
                        group_permissions: file.group_permissions,
                        user_permissions: file.user_permissions
                    } : file
                )
            );
            const token = client ? await getToken(client) : undefined;
            const permissionsData = items.map(item => ({
                action: "updatePermissions",
                file_path: item.file_path,
                group_permissions: item.group_permissions,
                user_permissions: item.user_permissions
            }));
            await updateFiles(token, permissionsData);
            handleSuccess("Permissions updated successfully!")

        } catch (error) {
            handleError(error, "Error updating permissions");
        }
    };

    const onRefreshClicked = () => {
        fetchData();
    };

    const onClearFiltersClicked = () => {
        setSelectedFileStates(selectableFileStateOptions.map(option => option.key.toString()));
        setSelectedFolders(selectableFolderOptions.map(option => option.key.toString()));
        setSelectedTags(selectableTagOptions.map(option => option.key.toString()));
        setFileNameSearch("");
    };


    function convertStatusToItems(fileList: FileUploadStatus[]) {
        const items: IDocument[] = [];

        for (let i = 0; i < fileList.length; i++) {
            const fileUserId = fileList[i].user_id;

            let fileExtension = fileList[i].file_name.split('.').pop();
            fileExtension = fileExtension == undefined ? 'Folder' : fileExtension.toUpperCase()
            const stateDescription = STATE_DESCRIPTION[fileList[i].state] || fileList[i].state;
            const iconName = FILE_ICONS[fileExtension.toLowerCase()] || 'txt';
            const iconUrl = `https://res-1.cdn.office.net/files/fabric-cdn-prod_20221209.001/assets/item-types/16/${iconName}.svg`;

            try {
                items.push({
                    key: fileList[i].id,
                    name: fileList[i].file_name,
                    file_path: fileList[i].file_path,
                    folder_name: fileList[i].folder_name === userData.userPrincipalName ? 'My Data' : fileList[i].folder_name,
                    user_id: fileUserId,
                    group_permissions: fileList[i].group_permissions,
                    user_permissions: fileList[i].user_permissions,
                    tags: fileList[i].tags,
                    iconUrl: iconUrl,
                    fileType: fileExtension,
                    state: fileList[i].state,
                    state_description: fileList[i].state_description || stateDescription,
                    upload_timestamp: fileList[i].start_timestamp,
                    modified_timestamp: fileList[i].state_timestamp,
                    value: fileList[i].id,
                    can_edit: fileUserId === userData.user_id || userData.is_admin,
                });
            }
            catch (error) {
                handleError(error, "An error occurred while converting files to list")
            }
        }
        return items;
    };

    const handleSelectionChange = (
        selectedItems: string[],
        setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>,
        selectableOptions: IComboBoxOption[],
        option?: IComboBoxOption
    ): void => {
        const selected = option?.selected;
        const currentSelectedOptionKeys = selectedItems?.filter(key => key !== 'selectAll');
        const selectableOptionsFiltered = selectableOptions.filter(
            option =>
            (option.itemType === SelectableOptionMenuItemType.Normal ||
                option.itemType === undefined),
        );
        const selectAllState = currentSelectedOptionKeys?.length === selectableOptionsFiltered.length;

        if (option) {
            if (option.itemType === SelectableOptionMenuItemType.SelectAll) {
                if (selectAllState) {
                    // Deselect all items, including "Select All"
                    setSelectedItems([]);
                } else {
                    // Select all items, including "Select All"
                    const updatedKeys = ['selectAll', ...selectableOptionsFiltered.map(o => o.key as string)];
                    setSelectedItems(updatedKeys);
                }
            } else {
                const updatedKeys = selected
                    ? [...currentSelectedOptionKeys, option!.key as string]
                    : currentSelectedOptionKeys.filter(k => k !== option.key);
                if (updatedKeys.length === selectableOptionsFiltered.length) {
                    updatedKeys.push('selectAll');
                }
                setSelectedItems(updatedKeys);
            }
        }
    };

    const onSelectedFileStateChange = (
        event: React.FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string,
    ): void => {
        handleSelectionChange(selectedFileStates, setSelectedFileStates, selectableFileStateOptions, option);
    };

    const onSelectedFoldersChange = (
        event: React.FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string,
    ): void => {
        handleSelectionChange(selectedFolders, setSelectedFolders, selectableFolderOptions, option);
    };

    const onSelectedTagsChange = (
        event: React.FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string,
    ): void => {
        handleSelectionChange(selectedTags, setSelectedTags, selectableTagOptions, option);
    };

    const onFileNameSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
        setFileNameSearch(newValue || "");
    };

    const onPageChange = (pageIndex: number) => {
        setCurrentPage(pageIndex + 1);
    };

    useEffect(() => {
        if (selectableFolders.length > 0) {
            const dropdownOptions: IComboBoxOption[] = [
                { key: 'selectAll', text: 'Select All', itemType: SelectableOptionMenuItemType.SelectAll },
                ...selectableFolders
            ];

            setSelectableFolderOptions(dropdownOptions);
            setSelectedFolders(dropdownOptions.map(option => option.key.toString()));
        };

        if (selectableTags.length > 0) {
            const dropdownOptions: IComboBoxOption[] = [
                { key: 'selectAll', text: 'Select All', itemType: SelectableOptionMenuItemType.SelectAll },
                ...selectableTags
            ];

            setSelectableTagOptions(dropdownOptions);
            setSelectedTags(dropdownOptions.map(option => option.key.toString()));
        };

        setSelectedFileStates(selectableFileStateOptions
            .filter(option => option.key !== 'Deleted' && option.key !== 'selectAll')
            .map(option => option.key.toString())
        );
    }, [selectableFolders, selectableTags]);

    useEffect(() => {
        // Only fetch data if the states are initialised and have values
        if (
            debouncedFileStates.length > 0 &&
            debouncedFolders.length > 0 &&
            debouncedTags.length > 0
        ) {
            setCurrentPage(1);  // Reset to the first page
            fetchData();
        }
    }, [debouncedFileStates, debouncedFolders, debouncedTags, debouncedFileNameSearch]);

    useEffect(() => {
        // Only fetch data if the states are initialised and have values
        if (
            debouncedFileStates.length > 0 &&
            debouncedFolders.length > 0 &&
            debouncedTags.length > 0
        ) {
            fetchData();
        }
    }, [currentPage]);

    return (
        <div className={styles.container}>
            <div className={`${styles.options} ${className ?? ""}`}>
                <ComboBox
                    label="File State:"
                    multiSelect
                    placeholder="Select file state(s)"
                    selectedKey={selectedFileStates ? selectedFileStates : undefined}
                    options={selectableFileStateOptions}
                    onChange={onSelectedFileStateChange}
                    className={styles.comboBox}
                    aria-label="File state options for filtering"
                />
                <ComboBox
                    label="Folder:"
                    multiSelect
                    placeholder="Select folder(s)"
                    selectedKey={selectedFolders ? selectedFolders : undefined}
                    options={selectableFolderOptions}
                    onChange={onSelectedFoldersChange}
                    className={styles.comboBox}
                    aria-label="Folder name options for filtering"
                />
                <ComboBox
                    label="Tags:"
                    multiSelect
                    placeholder="Select file tag(s)"
                    selectedKey={selectedTags ? selectedTags : undefined}
                    options={selectableTagOptions}
                    onChange={onSelectedTagsChange}
                    className={styles.comboBox}
                    aria-label="Tag options for filtering"
                />
                <Stack className={styles.searchBoxContainer}>
                    <Label className={styles.label}>File Name:</Label>
                    <SearchBox
                        placeholder="Search by file name"
                        value={fileNameSearch}
                        onChange={onFileNameSearchChange}
                        styles={{ root: { maxWidth: 300 } }}
                        aria-label="Search files by name"
                    />
                </Stack>
                <div className={styles.buttonArea}>
                    <div className={useDarkMode ? styles.buttonDark : styles.button} onClick={onRefreshClicked} aria-label="Refresh displayed file statuses">
                        <ArrowClockwise24Filled className={styles.buttonIcon} />
                        <span className={styles.buttonText}>Refresh</span>
                    </div>
                    <div className={useDarkMode ? styles.buttonDark : styles.button} onClick={onClearFiltersClicked} aria-label="Clear filters">
                        <FilterDismiss24Filled className={styles.buttonIcon} />
                        <span className={styles.buttonText}>Clear Filters</span>
                    </div>
                </div>
                {files && files.length > 0 && (
                    <div className={styles.pageInfo}>
                        Showing {firstRecordOnPage}-{lastRecordOnPage} of {totalCount} records.
                    </div>
                )}
            </div>
            {isLoading ? (
                <animated.div style={{ ...animatedStyles }}>
                    <Stack className={styles.loadingContainer} verticalAlign="space-between">
                        <Stack.Item grow>
                            <p className={styles.loadingText}>
                                Getting file statuses
                                <span className={styles.loadingdots} />
                            </p>
                        </Stack.Item>
                    </Stack>
                </animated.div>
            ) : (files && files.length > 0 ? (
                <div className={styles.resultspanel}>
                    <DocumentsDetailList
                        items={files == undefined ? [] : files}
                        onFilesSorted={onFilesSorted}
                        onFileDelete={onFileDelete}
                        onSaveTags={onSaveTags}
                        onSavePermissions={onSavePermissions}
                        isDeleting={isDeleting}
                        isAdmin={userData.is_admin}
                        useDarkMode={useDarkMode}
                    />
                    <Pagination
                        selectedPageIndex={currentPage - 1}
                        totalItemCount={totalCount}
                        onPageChange={onPageChange}
                        pageCount={Math.ceil(totalCount / itemsPerPage)}
                        format="buttons"
                    />
                </div>
            ) : (
                <Stack className={styles.loadingContainer} verticalAlign="space-between">
                    <Stack.Item grow>
                        <p className={styles.loadingText}>
                            No data available with the provided filters.
                        </p>
                    </Stack.Item>
                </Stack>
            ))}
        </div>
    );
};